import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  FileText, Package, Download, CheckCircle, ArrowLeft, ArrowRight,
  ShoppingCart, Eye, Lock, Star, ChevronLeft, ChevronRight,
  GraduationCap, Truck, Briefcase, Building2, Shield, Headphones
} from 'lucide-react';
import SEO from '../components/shared/SEO';

// Template Packet Data with full details
const TEMPLATE_PACKETS = {
  'cdl-success': {
    id: 'cdl-success',
    title: 'CDL Success Packet',
    subtitle: 'Everything You Need to Pass Your CDL Exam',
    description: 'The ultimate study companion for aspiring CDL drivers. This comprehensive packet includes all the checklists, guides, and study materials you need to ace your CDL exam on the first try. From pre-trip inspections to backing maneuvers, we\'ve got you covered.',
    icon: GraduationCap,
    color: '#D4AF37',
    price: 25,
    audience: 'CDL Students & New Drivers',
    items: [
      { name: 'Pre-Trip Inspection Checklist', description: 'Complete 17-point inspection guide with all DOT requirements' },
      { name: 'CDL Study Planner', description: '4-week structured study plan covering all exam topics' },
      { name: 'CDL Test Prep Cheat Sheet', description: 'Key numbers, rules, and facts you need to memorize' },
      { name: 'Air Brakes Quick Guide', description: 'Air brake system components and testing procedures' },
      { name: 'Backing Maneuver Guide', description: 'Step-by-step instructions for straight, offset, and alley dock backing' },
      { name: 'Road Test Tips Sheet', description: 'Common mistakes to avoid and pro tips for test day' },
      { name: 'CDL Glossary', description: '50+ industry terms and definitions' }
    ],
    benefits: [
      'Pass your CDL exam on the first attempt',
      'Save money on retakes and extra training',
      'Build confidence with structured preparation',
      'Reference materials you\'ll use throughout your career'
    ],
    previewCount: 3
  },
  'trucking-operations': {
    id: 'trucking-operations',
    title: 'Trucking Operations Packet',
    subtitle: 'Track Your Business Like a Pro',
    description: 'Stop guessing and start knowing exactly where your money goes. This operations packet gives you professional-grade tracking tools used by successful owner-operators to maximize profits and minimize waste.',
    icon: Truck,
    color: '#C0C0C0',
    price: 25,
    audience: 'Owner Operators & Fleet Owners',
    items: [
      { name: 'Cost Per Mile Calculator', description: 'Know your true operating cost with fixed and variable expense tracking' },
      { name: 'Load Profit Calculator', description: 'Instantly calculate profit margins on any load' },
      { name: 'Maintenance Tracker', description: 'Preventive maintenance schedule with service intervals' },
      { name: 'Fuel Log Tracker', description: 'Track fuel purchases, MPG, and identify efficiency trends' },
      { name: 'Weekly Revenue Tracker', description: 'Monitor your income and expenses week by week' },
      { name: 'Expense Tracker', description: 'Categorized monthly expense tracking for tax preparation' },
      { name: 'KPI Tracking Sheet', description: 'Key performance indicators every operator should monitor' }
    ],
    benefits: [
      'Know your exact cost per mile',
      'Identify profitable vs. unprofitable loads',
      'Maximize tax deductions with organized records',
      'Make data-driven business decisions'
    ],
    previewCount: 3
  },
  'freight-broker': {
    id: 'freight-broker',
    title: 'Freight Broker Packet',
    subtitle: 'Land Shippers & Manage Carriers',
    description: 'Everything you need to build a successful freight brokerage. From cold calling scripts to carrier agreements, this packet provides the professional tools and templates used by top-producing brokers.',
    icon: Briefcase,
    color: '#D4AF37',
    price: 25,
    audience: 'Freight Brokers & Agents',
    items: [
      { name: 'Shipper Outreach Email Templates', description: 'Proven email templates for initial contact and follow-up' },
      { name: 'Cold Call Script for Shippers', description: 'Word-for-word script with objection handlers' },
      { name: 'Carrier Vetting Checklist', description: 'Complete 12-point carrier qualification checklist' },
      { name: 'Broker-Carrier Agreement Template', description: 'Professional contract with all essential clauses' },
      { name: 'Load Tracking Sheet', description: 'Organize and monitor all active shipments' },
      { name: 'Profit/Margin Calculator', description: 'Calculate margins and ensure profitable loads' },
      { name: 'Follow-Up Email Templates', description: 'Stay top-of-mind with professional follow-up sequences' }
    ],
    benefits: [
      'Close more shipper accounts',
      'Protect yourself with proper agreements',
      'Vet carriers like a pro',
      'Organize your operations for scale'
    ],
    previewCount: 3
  },
  'authority-builder': {
    id: 'authority-builder',
    title: 'Authority Builder Packet',
    subtitle: 'Launch Your Trucking Authority',
    description: 'Starting your own trucking company? This comprehensive startup kit walks you through every step from business formation to your first load. Don\'t miss a single requirement.',
    icon: Building2,
    color: '#CD7F32',
    price: 25,
    audience: 'New Trucking Businesses',
    items: [
      { name: '90-Day Startup Checklist', description: 'Week-by-week action plan for your first 90 days' },
      { name: 'Authority Setup Checklist', description: 'MC, DOT, BOC-3, UCR, IFTA - complete requirements list' },
      { name: 'Startup Budget Planner', description: 'Estimate your total startup costs with itemized breakdown' },
      { name: 'Insurance Comparison Sheet', description: 'Compare quotes and coverage from multiple providers' },
      { name: 'Broker Setup Packet Template', description: 'Professional packet to send to brokers' },
      { name: 'Carrier Packet Template', description: 'All documents brokers need to set you up' },
      { name: 'Business Setup Checklist', description: 'LLC formation, EIN, banking, and legal requirements' }
    ],
    benefits: [
      'Avoid costly compliance mistakes',
      'Launch faster with a clear roadmap',
      'Look professional from day one',
      'Save thousands on consulting fees'
    ],
    previewCount: 3
  },
  'compliance': {
    id: 'compliance',
    title: 'Compliance Packet',
    subtitle: 'Stay DOT Compliant & Audit-Ready',
    description: 'Don\'t wait for a DOT audit to find out you\'re missing documents. This compliance packet ensures you have every required document organized and ready for inspection.',
    icon: Shield,
    color: '#C0C0C0',
    price: 25,
    audience: 'Fleet Managers & Safety Directors',
    items: [
      { name: 'DOT Audit Checklist', description: 'Complete list of documents auditors will request' },
      { name: 'Driver Qualification File Checklist', description: 'Every document required in a DQ file' },
      { name: 'Drug & Alcohol Log Template', description: 'Track all required testing with proper documentation' },
      { name: 'Accident Report Template', description: 'Comprehensive accident documentation form' },
      { name: 'Safety Meeting Template', description: 'Conduct and document required safety meetings' },
      { name: 'Monthly Compliance Tracker', description: 'Stay on top of recurring compliance tasks' },
      { name: 'Document Retention Checklist', description: 'Know what to keep and for how long' }
    ],
    benefits: [
      'Pass DOT audits with confidence',
      'Avoid fines and penalties',
      'Protect your authority',
      'Create a culture of safety'
    ],
    previewCount: 3
  },
  'dispatch': {
    id: 'dispatch',
    title: 'Dispatch Packet',
    subtitle: 'Manage Loads, Carriers & Brokers',
    description: 'Whether you\'re dispatching your own trucks or running a dispatch service, this packet provides all the tools you need to operate professionally and efficiently.',
    icon: Headphones,
    color: '#D4AF37',
    price: 25,
    audience: 'Dispatchers & Dispatch Services',
    items: [
      { name: 'Dispatch Service Agreement', description: 'Professional contract for dispatch services' },
      { name: 'Carrier Onboarding Packet', description: 'Checklist and forms for new carrier setup' },
      { name: 'Broker Call Script', description: 'How to call brokers and book loads effectively' },
      { name: 'Rate Negotiation Script', description: 'Proven tactics for getting better rates' },
      { name: 'Dispatch Workflow Checklist', description: 'Step-by-step process for every load' },
      { name: 'Load Tracker Sheet', description: 'Organize all active loads in one place' },
      { name: 'Invoice Template', description: 'Professional invoice with all required fields' },
      { name: 'Broker Contact Tracker', description: 'Build your broker network with organized contacts' }
    ],
    benefits: [
      'Operate like a professional dispatch service',
      'Never miss a step on any load',
      'Build strong broker relationships',
      'Scale your dispatch operations'
    ],
    previewCount: 3
  }
};

const BUNDLE_PRICE = 125;

const ToolkitProductPage = () => {
  const { packetId } = useParams();
  const navigate = useNavigate();
  const [currentPreview, setCurrentPreview] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(true);
  
  const packet = TEMPLATE_PACKETS[packetId];
  
  if (!packet) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-white text-2xl font-bold mb-4">Product Not Found</h1>
          <Link to="/toolkit" className="text-[#D4AF37] hover:underline">
            ← Back to Resources
          </Link>
        </div>
      </div>
    );
  }
  
  const Icon = packet.icon;
  const previewImages = Array.from({ length: packet.previewCount }, (_, i) => 
    `/templates/previews/${packet.id}_preview_${i + 1}.png`
  );
  
  const nextPreview = () => {
    setCurrentPreview((prev) => (prev + 1) % previewImages.length);
    setIsImageLoading(true);
  };
  
  const prevPreview = () => {
    setCurrentPreview((prev) => (prev - 1 + previewImages.length) % previewImages.length);
    setIsImageLoading(true);
  };

  // Get other packets for "You May Also Like"
  const otherPackets = Object.values(TEMPLATE_PACKETS)
    .filter(p => p.id !== packet.id)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="toolkit-product-page">
      <SEO 
        title={`${packet.title} | Queen B Resources`}
        description={packet.description}
      />

      {/* Breadcrumb */}
      <div className="bg-[#050505] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/toolkit" className="text-white/50 hover:text-white transition-colors">
              Queen B Resources
            </Link>
            <span className="text-white/30">/</span>
            <span className="text-[#D4AF37]">{packet.title}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            
            {/* Left Column - Preview Gallery */}
            <div>
              {/* Main Preview Image */}
              <div className="relative bg-[#121212] border border-white/10 aspect-[8.5/11] mb-4 overflow-hidden">
                {isImageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-[#121212]">
                    <div className="w-8 h-8 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" />
                  </div>
                )}
                <img 
                  src={previewImages[currentPreview]}
                  alt={`${packet.title} Preview ${currentPreview + 1}`}
                  className={`w-full h-full object-contain ${isImageLoading ? 'opacity-0' : 'opacity-100'} transition-opacity`}
                  onLoad={() => setIsImageLoading(false)}
                  onError={(e) => {
                    e.target.src = '/templates/previews/placeholder.png';
                    setIsImageLoading(false);
                  }}
                />
                
                {/* Navigation Arrows */}
                <button 
                  onClick={prevPreview}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/70 hover:bg-black flex items-center justify-center transition-colors"
                  aria-label="Previous preview"
                >
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <button 
                  onClick={nextPreview}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/70 hover:bg-black flex items-center justify-center transition-colors"
                  aria-label="Next preview"
                >
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
                
                {/* Page Indicator */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 px-3 py-1 text-white text-sm">
                  {currentPreview + 1} / {previewImages.length}
                </div>
                
                {/* Watermark Notice */}
                <div className="absolute top-4 right-4 bg-[#D4AF37]/90 text-black text-xs font-bold px-2 py-1 flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  PREVIEW
                </div>
              </div>
              
              {/* Thumbnail Strip */}
              <div className="flex gap-2 overflow-x-auto pb-2">
                {previewImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => { setCurrentPreview(idx); setIsImageLoading(true); }}
                    className={`flex-shrink-0 w-16 h-20 border-2 transition-all ${
                      idx === currentPreview 
                        ? 'border-[#D4AF37]' 
                        : 'border-white/10 hover:border-white/30'
                    }`}
                  >
                    <img 
                      src={img}
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = '/templates/previews/placeholder.png'; }}
                    />
                  </button>
                ))}
              </div>
              
              <p className="text-white/40 text-xs mt-2 text-center">
                Preview images are watermarked. Purchased files are clean, high-quality PDFs.
              </p>
            </div>
            
            {/* Right Column - Product Details */}
            <div>
              {/* Product Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <div 
                    className="w-12 h-12 rounded flex items-center justify-center"
                    style={{ backgroundColor: `${packet.color}20`, border: `1px solid ${packet.color}40` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: packet.color }} />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-wider">{packet.audience}</p>
                    <h1 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-white uppercase">
                      {packet.title}
                    </h1>
                  </div>
                </div>
                <p className="text-[#D4AF37] text-lg">{packet.subtitle}</p>
              </div>
              
              {/* Price */}
              <div className="bg-[#121212] border border-white/10 p-6 mb-6">
                <div className="flex items-end gap-3 mb-4">
                  <span className="text-[#D4AF37] text-4xl font-bold">${packet.price}</span>
                  <span className="text-white/50 text-sm mb-1">one-time purchase</span>
                </div>
                
                <Link
                  to={`/toolkit/checkout?packets=${packet.id}`}
                  className="w-full py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#B8962E] transition-colors flex items-center justify-center gap-2 mb-4"
                  data-testid="buy-now-btn"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Buy Now - ${packet.price}
                </Link>
                
                {/* Bundle Upsell */}
                <div className="border-t border-white/10 pt-4">
                  <p className="text-white/60 text-sm mb-2">Want more value?</p>
                  <Link
                    to="/toolkit/checkout?type=bundle"
                    className="text-[#D4AF37] text-sm font-bold hover:underline flex items-center gap-1"
                  >
                    Get all 6 packets for ${BUNDLE_PRICE} (Save $25)
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
              
              {/* Description */}
              <div className="mb-8">
                <h2 className="text-white font-bold text-lg mb-3">About This Packet</h2>
                <p className="text-white/70 leading-relaxed">{packet.description}</p>
              </div>
              
              {/* Benefits */}
              <div className="mb-8">
                <h2 className="text-white font-bold text-lg mb-3">Why You Need This</h2>
                <ul className="space-y-2">
                  {packet.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-white/70">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          
          {/* What's Included - Full Width */}
          <div className="mt-16">
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase mb-6 text-center">
              What's Included
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {packet.items.map((item, idx) => (
                <div 
                  key={idx}
                  className="bg-[#121212] border border-white/10 p-4 hover:border-[#D4AF37]/30 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#D4AF37]/10 rounded flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-[#D4AF37]" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-sm mb-1">{item.name}</h3>
                      <p className="text-white/50 text-xs">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* You May Also Like */}
          <div className="mt-16 pt-16 border-t border-white/10">
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase mb-6 text-center">
              You May Also Like
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {otherPackets.map((p) => {
                const OtherIcon = p.icon;
                return (
                  <Link
                    key={p.id}
                    to={`/toolkit/${p.id}`}
                    className="bg-[#121212] border border-white/10 p-6 hover:border-[#D4AF37]/50 transition-all group"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div 
                        className="w-10 h-10 rounded flex items-center justify-center"
                        style={{ backgroundColor: `${p.color}20`, border: `1px solid ${p.color}40` }}
                      >
                        <OtherIcon className="w-5 h-5" style={{ color: p.color }} />
                      </div>
                      <div>
                        <h3 className="text-white font-bold group-hover:text-[#D4AF37] transition-colors">{p.title}</h3>
                        <p className="text-white/40 text-xs">{p.audience}</p>
                      </div>
                    </div>
                    <p className="text-white/60 text-sm mb-3 line-clamp-2">{p.subtitle}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-[#D4AF37] font-bold">${p.price}</span>
                      <span className="text-white/40 text-sm group-hover:text-[#D4AF37] transition-colors">
                        View Details →
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
          
          {/* Final CTA */}
          <div className="mt-16 text-center">
            <div className="inline-block bg-[#121212] border border-white/10 p-8">
              <h3 className="text-white font-bold text-xl mb-2">Ready to get started?</h3>
              <p className="text-white/60 mb-6">Instant download after purchase. Lifetime access.</p>
              <Link
                to={`/toolkit/checkout?packets=${packet.id}`}
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#B8962E] transition-colors"
              >
                <Download className="w-5 h-5" />
                Get {packet.title} - ${packet.price}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ToolkitProductPage;
